import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import closeIcon from "../../../img/closeIcon.svg";
import { Button } from "../../Button/Button";
import styles from "./GenerateCommentsModal.module.scss";
import { useCloseModal } from "../hooks";
import { useLanguagesCode } from "../../../store/hooks";
import { TextArea } from "../../TextArea/TextArea";
import { Select } from "../../Select/Select";
import { getGeneratedComments } from "../../../api/localization";
import { Loader } from "../../Loader/Loader";
import { StyledInput } from "../../FormInput/FormInput.styled";

type Props = {
  hideModal: () => void;
  onCommentsGenerated?: (comments: CommentItem[]) => void;
};

type GenerateCommentsResponse = {
  success: boolean;
  message: string;
};

type CommentItem = {
  id: number;
  pwa: number;
  avatar: string | null;
  author: string;
  rating: number;
  likes: number;
  date: string;
  text: string;
  reply: string | null;
};

type WebSocketResponse = {
  event_name: "comments_generated";
  message: {
    step?:
      | "started"
      | "comments_generated"
      | "avatars_generating"
      | "saving"
      | "completed"
      | "failed";
    success?: boolean;
    message: string;
    comments?: CommentItem[];
  };
};

export const GenerateCommentModal: React.FC<Props> = ({
  hideModal,
  onCommentsGenerated,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<WebSocket | null>(null);

  useCloseModal(modalRef, hideModal);

  const pwaId = Number(window.location.pathname.split("/").pop());

  const [step, setStep] = useState<"form" | "loading" | "result">("form");
  const [isSocketConnected, setIsSocketConnected] = useState(false);

  const [progressTitle, setProgressTitle] = useState("");
  const [progressText, setProgressText] = useState("");

  const [resultType, setResultType] = useState<"success" | "error" | null>(null);
  const [resultTitle, setResultTitle] = useState("");
  const [resultMessage, setResultMessage] = useState("");
  const [generatedCount, setGeneratedCount] = useState<number | null>(null);
  const [previewText, setPreviewText] = useState("");

  const [commentsCount, setCommentsCount] = useState<string>("1");
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");
  const [languageLabel, setLanguageLabel] = useState<string>("");
  const [textareaKeywords, setTextareaKeywords] = useState("");
  const [tone, setTone] = useState<string>("positive");
  const [geo, setGeo] = useState("");
  const [platform, setPlatform] = useState("");
  const [audience, setAudience] = useState("");
  const [avatarGeneration, setAvatarGeneration] = useState(false);

  const [errors, setErrors] = useState<{
    count?: string;
  }>({});

  const languages = useLanguagesCode();
  const isCompactState = step === "loading" || step === "result";

  useEffect(() => {
    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    const wsUrl = `${protocol}://${window.location.host}/websocket/comment/comments/${pwaId}/`;

    const socket = new WebSocket(wsUrl);
    socketRef.current = socket;

    socket.onopen = () => {
      setIsSocketConnected(true);
      console.log("WS connected");
    };

    socket.onmessage = (event) => {
      try {
        console.log("WS raw message:", event.data);

        const data: WebSocketResponse = JSON.parse(event.data);

        if (data.event_name !== "comments_generated") {
          return;
        }

        const payload = data.message;

        console.log("WS parsed payload:", payload);

        if (payload.step === "started") {
          setStep("loading");
          setProgressTitle("Генерация комментариев");
          setProgressText(payload.message);
          return;
        }

        if (payload.step === "comments_generated") {
          setStep("loading");
          setProgressTitle("Комментарии сгенерированы");
          setProgressText(payload.message);
          return;
        }

        if (payload.step === "avatars_generating") {
          setStep("loading");
          setProgressTitle("Генерация фотографий");
          setProgressText(payload.message);
          return;
        }

        if (payload.step === "saving") {
          setStep("loading");
          setProgressTitle("Сохраняем результат");
          setProgressText(payload.message);
          return;
        }

        if (
          payload.step === "completed" ||
          (payload.success === true && Array.isArray(payload.comments))
        ) {
          const comments = payload.comments || [];
          const count = comments.length;
          const firstCommentText = comments[0]?.text || "";

          setStep("result");
          setResultType("success");
          setResultTitle("Комментарии готовы");
          setResultMessage(payload.message || "Генерация завершена");
          setGeneratedCount(count);
          setPreviewText(firstCommentText);

          if (onCommentsGenerated && comments.length > 0) {
            onCommentsGenerated(comments);
          }

          return;
        }

        if (payload.step === "failed" || payload.success === false) {
          setStep("result");
          setResultType("error");
          setResultTitle("Ошибка");
          setResultMessage(
            payload.message || "Не удалось сгенерировать комментарии"
          );
          setGeneratedCount(null);
          setPreviewText("");
          return;
        }
      } catch (error) {
        console.error("WS parse error:", error);
        setStep("result");
        setResultType("error");
        setResultTitle("Ошибка");
        setResultMessage("Ошибка обработки ответа сервера");
        setGeneratedCount(null);
        setPreviewText("");
      }
    };

    socket.onerror = (event) => {
      console.error("WS error:", event);
    };

    socket.onclose = (event) => {
      setIsSocketConnected(false);
      console.log("WS closed:", event.code, event.reason);
    };

    return () => {
      socket.close();
      socketRef.current = null;
    };
  }, [pwaId, onCommentsGenerated]);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setTextareaKeywords(event.target.value);
  };

  const handleClose = () => {
    const shouldReload = resultType === "success" && (generatedCount ?? 0) > 0;

    hideModal();

    if (shouldReload) {
      window.location.reload();
    }
  };

  const handleGenerateComments = async () => {
    try {
      if (!isSocketConnected) {
        setStep("result");
        setResultType("error");
        setResultTitle("Нет соединения");
        setResultMessage("WebSocket ещё не подключён. Попробуйте ещё раз.");
        return;
      }

      setStep("loading");
      setProgressTitle("Генерация комментариев");
      setProgressText("Запускаем генерацию комментариев...");

      setResultType(null);
      setResultTitle("");
      setResultMessage("");
      setGeneratedCount(null);
      setPreviewText("");

      const res: GenerateCommentsResponse = await getGeneratedComments({
        pwa_id: pwaId,
        text: textareaKeywords,
        geo,
        language: languageLabel,
        count: Number(commentsCount),
        avatar_generation: avatarGeneration,
        tone,
        platform: platform || undefined,
        audience: audience || undefined,
      });

      if (!res.success) {
        setStep("result");
        setResultType("error");
        setResultTitle("Ошибка");
        setResultMessage(
          res.message || "Не удалось запустить генерацию комментариев"
        );
      }
    } catch (error) {
      console.error("Ошибка генерации комментариев:", error);
      setStep("result");
      setResultType("error");
      setResultTitle("Ошибка");
      setResultMessage("Ошибка при генерации комментариев");
    }
  };

  return (
    <div className="modal-background">
      <div
        className={`${styles.wrapper} ${
          isCompactState ? styles.wrapperCompact : ""
        }`}
        ref={modalRef}
      >
        <div className={styles.modalTitleContainer}>
          <div className={styles.modalTitle}>Генерация комментариев</div>
          <img
            src={closeIcon}
            alt="close"
            className={styles.modalCloseIcon}
            onClick={handleClose}
          />
        </div>

        <div
          className={`${styles.contentContainer} ${
            isCompactState ? styles.contentContainerCompact : ""
          }`}
        >
          {step === "form" && (
            <div className={styles.formContainer}>
              <div>
                <label className={styles.inputLabel}>Количество комментариев</label>
                <StyledInput
                  $isvalid={!errors.count}
                  variant="standard"
                  fullWidth
                  InputProps={{ disableUnderline: true }}
                  value={commentsCount}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (Number(value) > 20) {
                      setErrors({ count: "Количество должно быть меньше 20" });
                    } else if (Number(value) < 1 && value !== "") {
                      setErrors({ count: "Количество должно быть положительным" });
                    } else if (
                      value !== "" &&
                      Number(value) !== Math.round(Number(value))
                    ) {
                      setErrors({ count: "Количество должно быть целым числом" });
                    } else {
                      setErrors({});
                    }

                    setCommentsCount(value);
                  }}
                  type="number"
                  placeholder="Введите количество"
                />

                {errors.count && (
                  <div className={styles.error}>{errors.count}</div>
                )}
              </div>

              <div>
                <label className={styles.inputLabel}>Ключевые слова</label>
                <TextArea
                  bg="white"
                  handleChange={handleChange}
                  value={textareaKeywords}
                />
              </div>

              <div>
                <label className={styles.inputLabel}>Тональность</label>
                <Select
                  value={tone}
                  options={[
                    { value: "positive", label: "Позитивные" },
                    { value: "negative", label: "Негативные" },
                    { value: "neutral", label: "Нейтральные" },
                  ]}
                  placeholder="Выберите тон"
                  onChange={(e) => setTone(e!.value)}
                />
              </div>

              <div>
                <label className={styles.inputLabel}>География</label>
                <StyledInput
                  $isvalid={true}
                  variant="standard"
                  fullWidth
                  InputProps={{ disableUnderline: true }}
                  value={geo}
                  onChange={(e) => setGeo(e.target.value)}
                  type="text"
                  placeholder="Введите географию аудитории"
                />
              </div>

              <div>
                <label className={styles.inputLabel}>Язык</label>
                <Select
                  value={selectedLanguage}
                  options={languages}
                  isSearchable
                  placeholder="Выберите язык"
                  onChange={(e) => {
                    setSelectedLanguage(e?.value || "");
                    setLanguageLabel(e?.label || "");
                  }}
                />
              </div>

              <div>
                <label className={styles.inputLabel}>Платформа</label>
                <StyledInput
                  $isvalid={true}
                  variant="standard"
                  fullWidth
                  InputProps={{ disableUnderline: true }}
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  type="text"
                  placeholder="Например: IOS или Android"
                />
              </div>

              <div>
                <label className={styles.inputLabel}>Аудитория</label>
                <StyledInput
                  $isvalid={true}
                  variant="standard"
                  fullWidth
                  InputProps={{ disableUnderline: true }}
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  type="text"
                  placeholder="Введите тип аудитории"
                />
              </div>

              <div className={styles.checkboxRow}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={avatarGeneration}
                    onChange={(e) => setAvatarGeneration(e.target.checked)}
                  />
                  <span>Генерировать аватары</span>
                </label>
              </div>
            </div>
          )}

          {step === "loading" && (
            <div className={styles.stateContainer}>
              <div className={styles.stateTitle}>{progressTitle}</div>
              <div className={styles.stateText}>{progressText}</div>
              <div className={styles.loaderContainer}>
                <Loader />
              </div>
            </div>
          )}

          {step === "result" && (
            <div className={styles.stateContainer}>
              <div
                className={`${styles.stateTitle} ${
                  resultType === "error" ? styles.errorTitle : styles.successTitle
                }`}
              >
                {resultTitle}
              </div>

              <div className={styles.stateText}>{resultMessage}</div>

              {resultType === "success" && generatedCount !== null && (
                <div className={styles.resultMeta}>
                  Сгенерировано комментариев: {generatedCount}
                </div>
              )}

              {resultType === "success" && previewText && (
                <div className={styles.previewBox}>
                  <div className={styles.previewTitle}>Пример комментария</div>
                  <div className={styles.previewText}>
                    {previewText.length > 140
                      ? `${previewText.slice(0, 140)}...`
                      : previewText}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {step !== "loading" && (
          <div className={styles.footer}>
            {step === "form" && (
              <Button
                disabled={!selectedLanguage || !!errors.count || !commentsCount}
                onClick={handleGenerateComments}
              >
                Сгенерировать
              </Button>
            )}

            {step === "result" && <Button onClick={handleClose}>Закрыть</Button>}
          </div>
        )}
      </div>
    </div>
  );
};